import {
  adminSystemId,
  camelToSpaces,
  encodeObjectId,
  objectTypes,
} from "@permutation-city/common";
import {
  ActionButton,
  BarLoader,
  Input,
  Select,
  useHotkeys,
} from "@permutation-city/ui";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useDustClient } from "../../hooks/useDustClient";
import { abi } from "../abi";

const DEPOSIT_ACTION = 5;

export function IncentivesForm() {
  const { data: dustClient } = useDustClient();
  const [objectId, setObjectId] = useState<string>("");
  const [tokenAmount, setTokenAmount] = useState<number>(1);
  const [reputationAmount, setReputationAmount] = useState<number>(1);

  const objectOptions = Object.values(objectTypes).map((object) => ({
    value: object.id,
    label: `${camelToSpaces(object.name).toUpperCase()} (ID: ${object.id})`,
  }));

  const incentiveMutation = useMutation({
    mutationFn: async () => {
      if (!dustClient) {
        throw new Error("Could not connect to Dust client");
      }
      if (!objectId) {
        throw new Error("Object ID is required");
      }

      const dataHash = encodeObjectId(objectId);
      const incentiveResult = await dustClient.provider.request({
        method: "systemCall",
        params: [
          {
            systemId: adminSystemId,
            abi,
            functionName: "setIncentive",
            args: [
              DEPOSIT_ACTION,
              dataHash,
              tokenAmount * 10 ** 18,
              reputationAmount * 10 ** 18,
            ],
          },
        ],
      });
      return incentiveResult;
    },
  });

  useHotkeys("^+S", () => incentiveMutation.mutate());

  return (
    <form className="w-full space-y-6 mt-2">
      <div>
        <Select
          name="object"
          options={objectOptions.map((opt) => opt.label)}
          placeholder="SELECT OBJECT…"
          onChange={(selectedLabel) => {
            const selected = objectOptions.find(
              (opt) => opt.label === selectedLabel,
            );
            if (selected) {
              setObjectId(selected.value.toString());
            }
          }}
        />
      </div>

      <div className="w-full flex gap-2">
        <div className="w-1/2">
          <Input
            label="TOKEN AMOUNT"
            value={tokenAmount.toString()}
            onChange={(e) => setTokenAmount(Number(e.target.value))}
            isBlink={false}
          />
        </div>

        <div className="w-1/2">
          <Input
            label="REPUTATION AMOUNT"
            value={reputationAmount.toString()}
            onChange={(e) => setReputationAmount(Number(e.target.value))}
            isBlink={false}
          />
        </div>
      </div>

      <ActionButton
        hotkey="^+S"
        onClick={() => incentiveMutation.mutate()}
        disabled={incentiveMutation.isPending}
      >
        Add incentive
      </ActionButton>

      {incentiveMutation.isPending && <BarLoader intervalRate={100} />}
      {incentiveMutation.isSuccess && (
        <p className="uppercase text-success">Successfully added incentive!</p>
      )}
      {incentiveMutation.isError && (
        <p className="uppercase text-danger">
          {incentiveMutation.error?.message || "Something went wrong"}
        </p>
      )}
    </form>
  );
}
